import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { itemsFor, CarDamageDiagram } from "../components/VehicleDamageDiagram";
import { printPublicCustody } from "../lib/publicCustodyPdf";
import { Package, User, Clock, Loader2, XCircle, FileText, Image as ImageIcon, ClipboardCheck, PenLine, ExternalLink, Gauge, Download } from "lucide-react";
import wzLogo from "../assets/workzone-logo.png";

export default function PublicCustody() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.publicCustody(id).then(setData).catch(() => setError(true)).finally(() => setLoading(false));
  }, [id]);

  const fileUrl = (fid) => api.publicCustodyFileUrl(id, fid);
  const insp = data?.inspection;
  const checklist = insp?.checklist || {};

  return (
    <div dir="rtl" className="min-h-screen bg-slate-50 flex items-start justify-center p-4 sm:p-8" data-testid="public-custody-page">
      <div className="w-full max-w-lg">
        <div className="flex items-center gap-2 justify-center mb-5 text-slate-800">
          <div className="h-10 w-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center p-1.5">
            <img src={wzLogo} alt="WorkZone" className="h-full w-full object-contain" />
          </div>
          <span className="font-heading font-extrabold text-lg">WorkZone</span>
        </div>

        {loading ? (
          <div className="py-20 text-center text-slate-400"><Loader2 className="h-7 w-7 animate-spin mx-auto" /></div>
        ) : error || !data ? (
          <div className="rounded-2xl bg-white border border-slate-200 p-8 text-center">
            <XCircle className="h-10 w-10 text-red-400 mx-auto mb-3" />
            <p className="text-slate-600 font-semibold">السجل غير موجود</p>
            <p className="text-sm text-slate-400 mt-1">قد يكون الرمز غير صحيح أو تم حذف العهدة.</p>
          </div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            className="rounded-3xl bg-white border border-slate-200 overflow-hidden shadow-sm">
            <div className="bg-gradient-to-l from-blue-700 to-blue-600 text-white p-6">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-xl bg-white/15 flex items-center justify-center">
                  <Package className="h-6 w-6" />
                </div>
                <div>
                  <p className="font-heading text-xl font-extrabold">{data.type}</p>
                  {data.identifier && <p className="text-blue-50 text-sm">{data.identifier}</p>}
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-3 text-sm text-blue-50">
                {data.branch && <span>الفرع: {data.branch}</span>}
                {data.org_name && <span>· {data.org_name}</span>}
                {data.chassis_number && <span>· الهيكل: {data.chassis_number}</span>}
                {data.color && <span>· اللون: {data.color}</span>}
              </div>
            </div>

            <div className="p-6">
              <div className="rounded-xl bg-slate-50 px-4 py-3 text-sm flex items-center gap-2 mb-5">
                <User className="h-4 w-4 text-blue-600" />
                {data.current_holder
                  ? <>بحوزة حالياً: <span className="font-semibold text-slate-800">{data.current_holder}</span></>
                  : <span className="text-slate-400">غير مُسلَّمة حالياً</span>}
              </div>

              <button type="button" data-testid="download-pdf-btn" onClick={() => printPublicCustody(data, id)}
                className="w-full mb-5 inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 transition-colors shadow-sm shadow-blue-600/20">
                <Download className="h-4 w-4" /> تحميل الملف الكامل PDF (المستندات + الفحص + الصور)
              </button>

              {/* المستندات */}
              {data.documents?.length > 0 && (
                <section className="mb-6" data-testid="public-documents">
                  <p className="text-xs font-semibold text-slate-500 mb-3 flex items-center gap-1.5">
                    <FileText className="h-3.5 w-3.5" /> أوراق ومستندات المركبة
                  </p>
                  <div className="space-y-2">
                    {data.documents.map((doc, i) => (
                      <div key={i} className="flex items-center justify-between gap-2 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5" data-testid={`public-doc-${i}`}>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-800">{doc.label}{doc.value ? `: ${doc.value}` : ""}</p>
                          {doc.expiry && <p className="text-xs text-slate-500">تنتهي: {doc.expiry}</p>}
                        </div>
                        {doc.file_id && (
                          <a href={fileUrl(doc.file_id)} target="_blank" rel="noreferrer"
                            data-testid={`public-doc-link-${i}`}
                            className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700 bg-blue-50 rounded-lg px-2.5 py-1.5">
                            عرض <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* صور المركبة */}
              {data.photos?.length > 0 && (
                <section className="mb-6" data-testid="public-photos">
                  <p className="text-xs font-semibold text-slate-500 mb-3 flex items-center gap-1.5">
                    <ImageIcon className="h-3.5 w-3.5" /> صور المركبة ({data.photos.length})
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {data.photos.map((fid, i) => (
                      <a key={i} href={fileUrl(fid)} target="_blank" rel="noreferrer" data-testid={`public-photo-${i}`}
                        className="block aspect-square rounded-xl overflow-hidden border border-slate-200 bg-slate-100">
                        <img src={fileUrl(fid)} alt={`صورة ${i + 1}`} className="h-full w-full object-cover" loading="lazy" />
                      </a>
                    ))}
                  </div>
                </section>
              )}

              {/* فحص التسليم */}
              {insp && (
                <section className="mb-6" data-testid="public-inspection">
                  <p className="text-xs font-semibold text-slate-500 mb-3 flex items-center gap-1.5">
                    <ClipboardCheck className="h-3.5 w-3.5" /> فحص التسليم
                  </p>
                  {insp.odometer != null && insp.odometer !== "" && (
                    <div className="mb-3 inline-flex items-center gap-1.5 text-sm text-slate-700 bg-slate-50 rounded-lg px-3 py-1.5">
                      <Gauge className="h-4 w-4 text-blue-600" /> قراءة العدّاد: <span className="font-semibold">{insp.odometer}</span> كم
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 mb-4">
                    {itemsFor(insp.kind).map((it) => (
                      <div key={it.key} className="flex items-center gap-2 text-sm text-slate-700">
                        <span className={`grid place-items-center h-4 w-4 rounded text-[10px] font-bold text-white ${checklist[it.key] ? "bg-emerald-500" : "bg-slate-300"}`}>
                          {checklist[it.key] ? "✓" : "✗"}
                        </span>
                        {it.label}
                      </div>
                    ))}
                  </div>

                  {insp.damages?.length > 0 && (
                    <div className="mb-3" data-testid="public-damages">
                      <p className="text-xs font-semibold text-slate-500 mb-2">مخطط الأضرار ({insp.damages.length})</p>
                      <CarDamageDiagram kind={insp.kind} damages={insp.damages} interactive={false} />
                      <ul className="mt-2 space-y-1">
                        {insp.damages.map((d, i) => (
                          <li key={i} className="text-xs text-slate-600 flex items-start gap-1.5">
                            <span className="shrink-0 h-4 w-4 grid place-items-center rounded-full bg-rose-600 text-white text-[10px] font-bold">{i + 1}</span>
                            {d.note || "ضرر بدون وصف"}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {insp.photo_ids?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs font-semibold text-slate-500 mb-2">صور وقت الاستلام</p>
                      <div className="grid grid-cols-3 gap-2">
                        {insp.photo_ids.map((fid, i) => (
                          <a key={i} href={fileUrl(fid)} target="_blank" rel="noreferrer" data-testid={`public-insp-photo-${i}`}
                            className="block aspect-square rounded-xl overflow-hidden border border-slate-200 bg-slate-100">
                            <img src={fileUrl(fid)} alt={`فحص ${i + 1}`} className="h-full w-full object-cover" loading="lazy" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {insp.note && <p className="text-sm text-slate-600 bg-slate-50 rounded-lg px-3 py-2">ملاحظة الفحص: {insp.note}</p>}
                </section>
              )}

              {/* التوقيع */}
              {data.current_signature_id && (
                <section className="mb-6" data-testid="public-signature">
                  <p className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1.5">
                    <PenLine className="h-3.5 w-3.5" /> توقيع المستلم
                  </p>
                  <div className="rounded-xl border border-slate-200 bg-white p-2 inline-block">
                    <img src={fileUrl(data.current_signature_id)} alt="التوقيع" className="h-24 object-contain" />
                  </div>
                </section>
              )}

              {/* سلسلة التسليم */}
              <p className="text-xs font-semibold text-slate-500 mb-3 flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" /> سلسلة التسليم
              </p>
              <div className="space-y-3">
                {[...data.assignments].reverse().map((a, i) => {
                  const isCurrent = !a.returned_at;
                  return (
                    <div key={i} className="flex gap-3">
                      <span className={`mt-1 h-3 w-3 rounded-full shrink-0 ${isCurrent ? "bg-blue-500 ring-4 ring-blue-100" : "bg-slate-300"}`} />
                      <div>
                        <p className="font-semibold text-slate-800 text-sm">{a.employee_name}</p>
                        <p className="text-xs text-slate-500">
                          استلمها {a.received_at}{a.returned_at ? ` — سلّمها ${a.returned_at}` : " — حتى الآن"}
                        </p>
                        {a.note && <p className="text-xs text-slate-600 mt-0.5">{a.note}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
